import React, { ReactElement } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  styled,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as tripSlice from '../../../data/trip.slice';
import { SortModel } from '../../../data/models/sort-model.model';
import ContentSearch from '../ContentSearch';
import {
  DataGridPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortItem,
} from '@mui/x-data-grid-pro';
import phoneUtils from '../../../services/phoneUtils';

const PREFIX = 'AssignDriverDialog';

export const classes = {
  content: `${PREFIX}-content`,
  grid: `${PREFIX}-grid`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface AssignDriverDialogProps {
  open: boolean;
  providerId: number;
  tripLegReferenceID: string;
  close: (saved: boolean) => void;
}

export default function AssignDriverDialog({
  open,
  providerId,
  tripLegReferenceID,
  close,
}: AssignDriverDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const assigning = useAppSelector(tripSlice.selectAssigningDriverToTrip);
  const assigned = useAppSelector(tripSlice.selectAssignedDriverToTrip);

  const loading = useAppSelector(tripSlice.selectLoadingTripEligibleDrivers);
  const driverList = useAppSelector(tripSlice.selectTripEligibleDriverList);
  const [searchFilter, setSearchFilter] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [sortModel, setSortModel] = React.useState<SortModel | null>(null);
  const [selectedDriverID, setSelectedDriverID] = React.useState<
    number | undefined
  >();

  React.useEffect(() => {
    dispatch(
      tripSlice.fetchTripEligibleDriverListAsync(
        providerId,
        pageSize,
        page,
        '',
        searchFilter,
        sortModel,
      ),
    );
  }, [searchFilter, page, pageSize, sortModel, dispatch]);

  React.useEffect(() => {
    if (assigned) {
      dispatch(tripSlice.setAssignedDriverToTrip(false));
      closeDialog(true);
    }
  }, [assigned]);

  const renderDriverPhone = (params: GridCellParams): ReactElement => {
    const phoneNumber = phoneUtils.getDriverPrimaryPhone(
      params.row.phoneNumbers,
    );
    return <>{phoneUtils.formatPhone(phoneNumber)}</>;
  };

  const renderDriverEmail = (params: GridCellParams): ReactElement => {
    const email = params.row.emailAddresses[0];
    return <>{email}</>;
  };

  const columns: GridColDef[] = [
    {
      field: 'driverLastName',
      headerName: t('drivers.lastName') || '',
      flex: 1,
    },
    {
      field: 'driverFirstName',
      headerName: t('drivers.firstName') || '',
      flex: 1,
    },
    {
      field: 'DriverPhone',
      headerName: t('common.phone') || '',
      flex: 1,
      sortable: false,
      renderCell: renderDriverPhone,
    },
    {
      field: 'DriverEmailAddress',
      headerName: t('common.email') || '',
      flex: 1,
      sortable: false,
      renderCell: renderDriverEmail,
    },
  ];

  const onSearchChange = (val: string) => {
    setSearchFilter(val);
  };

  const onSortModelChange = (sortModel: GridSortItem[]) => {
    const newModel = sortModel.length
      ? ({
          column: sortModel[0].field,
          direction: sortModel[0].sort,
        } as SortModel)
      : null;
    setSortModel(newModel);
  };

  const onPageChange = (pg: number) => {
    setPage(pg);
  };

  const onPageSizeChange = (pgSize: number) => {
    setPageSize(pgSize);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== driverList.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  const onSelectDriver = (model: GridRowSelectionModel) => {
    setSelectedDriverID(model.length ? (model[0] as number) : undefined);
  };

  const assignDriver = () => {
    if (providerId && selectedDriverID && tripLegReferenceID) {
      dispatch(
        tripSlice.assignDriverToTripAsync(selectedDriverID, tripLegReferenceID),
      );
    }
  };

  const closeDialog = (saved: boolean) => {
    setSearchFilter('');
    setPage(1);
    setSortModel(null);
    setSelectedDriverID(undefined);
    close(saved);
  };

  return (
    <StyledDialog open={open} fullWidth={true} maxWidth="lg">
      <DialogTitle>{t('trips.assignDriver')}</DialogTitle>
      <DialogContent className={classes.content}>
        <ContentSearch
          icon={<PersonIcon />}
          placeholder={t('trips.searchDriver')}
          currentSearchFilter={searchFilter}
          onSearchChange={onSearchChange}></ContentSearch>
        <DataGridPro
          getRowId={(row: any) => row.driverID}
          rows={driverList.items}
          columns={columns}
          pagination={true}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: driverList.currentPage - 1, pageSize }}
          rowCount={driverList.itemCount}
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          sortModel={
            sortModel
              ? [
                  {
                    field: sortModel.column,
                    sort: sortModel.direction,
                  },
                ]
              : []
          }
          onPaginationModelChange={paginationChange}
          onSortModelChange={onSortModelChange}
          onRowSelectionModelChange={onSelectDriver}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={assignDriver}
          disabled={!selectedDriverID || assigning}>
          {t('trips.assign')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => closeDialog(false)}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
